import { nanoid } from "nanoid";

/**
 * ID Prefix Reference
 * ===================
 * r-      Round (grant allocation cycle)
 * a-      Action (event in the event log)
 * audit-  General audit log
 * p-      Proposal (grant application)
 * field-  Proposal field (form question)
 * form-   Proposal form (collection of fields)
 * team-   Round-independent team
 * te-     Team event
 * c-      Client (browser session)
 * f-      Funder (evaluator who allocates budget to recommenders)
 * rec-    Recommender (evaluator who rates proposals)
 * u-      User (person's account)
 * rm-     Round member (user's membership in a round)
 * fr-     Field response (proposal's answer to a field)
 * at-     Agent token (API access token)
 * aq-     AI question definition (saved custom proposal question)
 * aqr-    AI question run (per-proposal AI answer)
 * saq-    Shared AI question definition (round-shared custom proposal question)
 * saqr-   Shared AI question run (shared per-proposal AI answer)
 * ans-    Note score (LLM-scored evaluator note)
 * t-      Transcript (uploaded meeting transcript)
 * tch-    Transcript custom chapters (human-authored chapter set)
 * rts-    Round transcript summary (combined summary across meetings)
 * kpe-    Key people extraction (LLM-extracted key people)
 * kir-    Key individual research report (web-researched diligence memo)
 * pse-    Proposal summary extraction (LLM-extracted proposal summary)
 * fc-     Fact check (LLM-extracted claims from evaluator notes)
 * fcr-    Fact check claim response (human response to fact-check claim)
 * ssc-    Scoring sheet config (round → spreadsheet mapping)
 * pns-    Proposal note summary (per-proposal aggregated summary)
 * rns-    Recommender note summary (per-recommender aggregated summary)
 * rpns-   Recommender proposal note summary (per-proposal pyramid summary)
 * pt-     Prompt template (DB-managed LLM prompt)
 * se-     Summary edit (human edit to a proposal note summary, in proposal_note_summary_edits)
 * pdre-   Proposal deep research edit (human edit to a proposal deep research report)
 * kire-   Key individual research report edit (human edit to a KI research report)
 * sr-     Summary review (approval/changes-requested, in artifact_reviews)
 * sc-     Summary comment (comment, in artifact_comments)
 * ncr-    Note change review (triage status, in note_change_reviews)
 * ncc-    Note change comment (comment, in note_change_comments)
 * sfi-    Summary feedback issue (categorized QA issue, in artifact_feedback_issues)
 * as-     Artifact state (workflow + active edit pointer per artifact)
 * scic-   Summary common issues cache (cached AI issue summaries by round/hash)
 * lfi-    Longform image (imported image stored from Google Docs)
 * vp-     Video poster (a thumbnail candidate for a video)
 * e-      Evaluator (generic, rarely used)
 * cs-     Connection session (server-observed socket session)
 * ps-     Presence segment (client-reported visible stretch)
 * dm-     Discussion message (Spec Grants comment or reply, minted by the client)
 */

// https://zelark.github.io/nano-id-cc/
// 8 characters: 2M IDs before a 1% chance of collision
// 9: 19M IDs before a 1% chance of collision
// 10: 152M IDs before a 1% chance of collision
// 11: 1B IDs before a 1% chance of collision
// 12: 9B IDs before a 1% chance of collision

export const generateRoundId = () => `r-${nanoid(8)}`;
export const generateActionId = () => `a-${nanoid(12)}`;
// Watch segment (one contiguous video playback stretch, SP-2690). Minted by
// the client ONCE when a segment opens and reused for every extension/resend
// — the upsert idempotency key.
export const generateWatchSegmentId = () => `ws-${nanoid(12)}`;
export const generateConnectionSessionId = () => `cs-${nanoid(12)}`;
export const generatePresenceSegmentId = () => `ps-${nanoid(12)}`;
// Minted by the client when a comment or reply is sent, so whatever is queued
// behind it (a reply, a reaction) names that message even before the server
// confirms it.
export const generateDiscussionMessageId = () => `dm-${nanoid(12)}`;
export const generateAuditLogId = () => `audit-${nanoid(12)}`;
export const generateProposalId = () => `p-${nanoid(10)}`;
export const generateProposalFieldId = () => `field-${nanoid(10)}`;
export const generateProposalFormId = () => `form-${nanoid(10)}`;
export const generateTeamId = () => `team-${nanoid(10)}`;
export const generateTeamEventId = () => `te-${nanoid(12)}`;

// A stable id derived from a seed, for the ONE place that must never mint a
// random id: rebuilding a round from its event history. Reducers replay the
// same events on every load, so any id they invent must be identical each time
// or the round's own data stops matching itself. New rounds record their ids
// in the creation event; this covers only legacy events created before that,
// keyed off the round id so a given round always rebuilds to the same value.
// Not a source of new ids.
const DETERMINISTIC_ID_ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_";
function deterministicSuffix(seed: string, size: number): string {
  let hash = 0x811c9dc5;
  let out = "";
  for (let i = 0; out.length < size; i++) {
    hash ^= seed.charCodeAt(i % seed.length) + i * 0x100;
    hash = Math.imul(hash, 0x01000193) >>> 0;
    out += DETERMINISTIC_ID_ALPHABET[hash % DETERMINISTIC_ID_ALPHABET.length];
  }
  return out;
}
export const deriveLegacyProposalId = (seed: string) =>
  `p-${deterministicSuffix(seed, 10)}`;
export const deriveLegacyProposalFormId = (seed: string) =>
  `form-${deterministicSuffix(seed, 10)}`;
export const generateClientId = () => `c-${nanoid(8)}`;
export const generateFunderId = () => `f-${nanoid(8)}`;
export const generateRecommenderId = () => `rec-${nanoid(8)}`;
export const generateUserId = () => `u-${nanoid(10)}`;
export const generateRoundMemberId = () => `rm-${nanoid(10)}`;
export const generateFieldResponseId = () => `fr-${nanoid(11)}`;
export const generateAgentTokenId = () => `at-${nanoid(12)}`;
export const generateAiQuestionDefinitionId = () => `aq-${nanoid(12)}`;
export const generateAiQuestionRunId = () => `aqr-${nanoid(12)}`;
export const generateSharedAiQuestionDefinitionId = () => `saq-${nanoid(12)}`;
export const generateSharedAiQuestionRunId = () => `saqr-${nanoid(12)}`;
export const generateNoteScoreId = () => `ans-${nanoid(12)}`;
export const generateTranscriptId = () => `t-${nanoid(10)}`;
export const generateTranscriptCustomChaptersId = () => `tch-${nanoid(12)}`;
export const generateVideoId = () => `v-${nanoid(10)}`;
export const generateVideoPosterId = () => `vp-${nanoid(12)}`;
/** A FormUploadFile row — also the unique segment of its object path. */
export const generateFormUploadFileId = () => `fu-${nanoid(12)}`;
export const generateRoundTranscriptSummaryId = () => `rts-${nanoid(10)}`;
export const generateKeyPeopleExtractionId = () => `kpe-${nanoid(12)}`;
export const generateKeyIndividualResearchReportId = () => `kir-${nanoid(12)}`;
export const generateProposalSummaryExtractionId = () => `pse-${nanoid(12)}`;
export const generateFactCheckId = () => `fc-${nanoid(12)}`;
export const generateFactCheckClaimResponseId = () => `fcr-${nanoid(12)}`;

export const generateNoteSentimentId = () => `ns-${nanoid(12)}`;
export const generateScoringSheetConfigId = () => `ssc-${nanoid(10)}`;
export const generateProposalNoteSummaryId = () => `pns-${nanoid(12)}`;
export const generateRecommenderNoteSummaryId = () => `rns-${nanoid(12)}`;
export const generateRecommenderProposalNoteSummaryId = () =>
  `rpns-${nanoid(12)}`;
export const generatePromptTemplateId = () => `pt-${nanoid(12)}`;
export const generateSummaryEditId = () => `se-${nanoid(12)}`;
export const generateProposalDeepResearchEditId = () => `pdre-${nanoid(12)}`;
export const generateKeyIndividualResearchReportEditId = () =>
  `kire-${nanoid(12)}`;
export const generateSummaryReviewId = () => `sr-${nanoid(12)}`;
export const generateSummaryCommentId = () => `sc-${nanoid(12)}`;
export const generateNoteChangeReviewId = () => `ncr-${nanoid(12)}`;
export const generateNoteChangeCommentId = () => `ncc-${nanoid(12)}`;
export const generateSummaryFeedbackIssueId = () => `sfi-${nanoid(12)}`;
export const generateArtifactStateId = () => `as-${nanoid(12)}`;
export const generateSummaryCommonIssuesCacheId = () => `scic-${nanoid(12)}`;
export const generateLongformImageId = () => `lfi-${nanoid(12)}`;

export const generateEvaluatorId = (
  evaluatorType: "recommender" | "funder",
) => {
  switch (evaluatorType) {
    case "recommender":
      return generateRecommenderId();
    case "funder":
      return generateFunderId();
    default:
      return `e-${nanoid(8)}`;
  }
};

export const generateTeamInvitationId = () => `ti-${nanoid(32)}`;
